<template>
  <div class="ai-msg" :class="[message.role, { error: message.error }]">
    <span v-if="message.role === 'assistant'" class="ai-msg-avatar" aria-hidden="true">
      <img src="@/assets/images/iaLOGO.png" alt="" />
    </span>

    <div class="ai-msg-col">
      <div class="ai-bubble">
        <div v-if="message.role === 'assistant'" class="ai-rich">
          <div
            v-for="(block, blockIndex) in blocks"
            :key="blockIndex"
            class="ai-block"
            :class="`ai-block-${block.type}`"
          >
            <span v-if="block.type === 'numbered'" class="ai-block-number">{{ block.number }}</span>
            <span v-if="block.type === 'bullet'" class="ai-block-dot"></span>
            <span class="ai-block-text">
              <template v-for="(part, partIndex) in parts(block.text)" :key="partIndex">
                <strong v-if="part.strong">{{ part.text }}</strong>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
          </div>
        </div>
        <div v-else class="ai-plain">{{ message.text }}</div>

        <div v-if="message.attachments && message.attachments.length" class="ai-msg-files">
          <span v-for="file in message.attachments" :key="file.name" class="ai-msg-file">
            <i class="fas fa-paperclip"></i>{{ file.name }}
          </span>
        </div>
      </div>

      <AssistantResultCards
        v-if="message.cards && message.cards.length"
        :cards="message.cards"
        @navigate="$emit('navigate', $event)"
      />

      <div class="ai-msg-meta">
        <span v-if="message.at">{{ time }}</span>
        <button v-if="message.error && retryable" type="button" class="ai-msg-action" @click="$emit('retry')">
          <i class="fas fa-redo-alt"></i> Réessayer
        </button>
        <button
          v-if="message.role === 'assistant' && !message.error"
          type="button"
          class="ai-msg-action ai-msg-copy"
          :aria-label="copied ? 'Copié' : 'Copier la réponse'"
          @click="copy"
        >
          <i :class="copied ? 'fas fa-check' : 'far fa-copy'"></i>
          {{ copied ? 'Copié' : 'Copier' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AssistantResultCards from './AssistantResultCards.vue';
import { textBlocks, inlineParts, plainText, formatTime } from './assistant-format';

export default {
  name: 'AssistantMessage',
  components: { AssistantResultCards },
  props: {
    message: { type: Object, required: true },
    retryable: { type: Boolean, default: false },
  },
  emits: ['navigate', 'retry'],
  data() {
    return { copied: false };
  },
  computed: {
    blocks() {
      return textBlocks(this.message.text, this.message.role);
    },
    time() {
      return formatTime(this.message.at);
    },
  },
  methods: {
    parts: inlineParts,
    async copy() {
      try {
        await navigator.clipboard.writeText(plainText(this.message.text));
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1600);
      } catch (error) {
        this.$snotify?.error('Copie impossible.');
      }
    },
  },
};
</script>

<style scoped>
.ai-msg {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.ai-msg.user {
  justify-content: flex-end;
}

.ai-msg-avatar {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--ai-line);
  background: var(--ai-surface);
  display: grid;
  place-items: center;
}

.ai-msg-avatar img {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.ai-msg-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 86%;
}

.ai-msg.user .ai-msg-col {
  align-items: flex-end;
}

.ai-bubble {
  padding: 10px 13px;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.ai-msg.assistant .ai-bubble {
  border: 1px solid var(--ai-line);
  border-radius: 4px 14px 14px 14px;
  background: var(--ai-surface);
  color: #1e293b;
}

.ai-msg.user .ai-bubble {
  border-radius: 14px 4px 14px 14px;
  background: var(--ai-primary);
  color: #fff;
}

.ai-msg.error .ai-bubble {
  border-color: #fecdd3;
  background: #fff1f2;
  color: #9f1239;
}

.ai-plain {
  white-space: pre-wrap;
}

.ai-rich {
  display: grid;
  gap: 6px;
}

.ai-block-space {
  height: 2px;
}

.ai-block-heading {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 700;
  color: var(--ai-ink);
}

.ai-block-bullet,
.ai-block-numbered {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 6px;
  align-items: start;
}

.ai-block-dot::before {
  content: '';
  display: block;
  width: 5px;
  height: 5px;
  margin: 8px auto 0;
  border-radius: 50%;
  background: var(--ai-primary);
}

.ai-block-number {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background: var(--ai-primary-soft);
  color: var(--ai-primary);
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.ai-block-text {
  min-width: 0;
}

.ai-block-text strong {
  font-weight: 700;
  color: var(--ai-ink);
}

.ai-msg-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.ai-msg-file {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 200px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-msg-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 20px;
  margin-top: 4px;
  padding: 0 2px;
  font-size: 11px;
  color: var(--ai-faint);
}

.ai-msg-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ai-muted);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.ai-msg-action:hover {
  color: var(--ai-primary);
}

.ai-msg.error .ai-msg-action {
  color: #be123c;
}

@media (hover: hover) {
  .ai-msg-copy {
    opacity: 0;
    transition: opacity 140ms ease;
  }

  .ai-msg:hover .ai-msg-copy,
  .ai-msg-copy:focus-visible {
    opacity: 1;
  }
}
</style>
